import { DATA_CONSUMED_KEY, DATA_GENERATED_KEY } from 'src/util/constants';
import logger from 'src/util/logger';
import {
  getAggregationDuration, getIntervalStart, hasTimeRangeOverlap, isValidTimeRange,
} from 'src/util/time/timeRange';

import { getPropertyDataInBatches } from './dataBatching';

/**
 * Maximum number of cached date-range segments.
 * See docs/design-docs/property-show-caching.md
 * This will be moved to a config as part of https://enosi.atlassian.net/browse/PT-1956
 */
export const MAX_CACHE_SEGMENTS = 5;

/** Module-level cache instance. Only one PropertyShow exists at a time. */
let cache = { propertyId: null, segments: {} };

/**
 * Create a fresh cache object for a given propertyId and assign it
 * to the module-level singleton.
 * @param {string} propertyId - The property UUID.
 * @returns {object} New cache object with empty segments.
 */
export const initialiseCache = (propertyId) => {
  cache = { propertyId, segments: {} };
  return cache;
};

/**
 * Return the current module-level cache singleton. Intended for
 * read-only consumers (e.g. cache-recovery flows that peek at the cache
 * without triggering a fetch via `getPropertyDataFromCache`).
 * @returns {object} The current cache `{ propertyId, segments }`.
 */
export const getCache = () => cache;

/**
 * Find a cached segment whose time range fully contains the requested
 * timeRange at the same aggregation level.
 * @param {object} segments - Object keyed by `startSeconds_finishSeconds`,
 *   each value having `start`, `finish` Luxon DateTime properties and
 *   `aggregation`.
 * @param {object} timeRange - Requested range with `start` and `finish`
 *   Luxon DateTimes.
 * @param {string} aggregation - Required aggregation level
 *   (e.g. 'PT30M', 'P1D').
 * @returns {object|null} The first containing segment, or null if no
 *   segment covers the full requested range.
 */
export const getSegmentDataFromCache = (segments, timeRange, aggregation) => {
  if (!segments || !timeRange?.start || !timeRange?.finish || !aggregation) {
    return null;
  }

  const requestedStart = timeRange.start.toSeconds();
  const requestedFinish = timeRange.finish.toSeconds();

  const keys = Object.keys(segments);
  for (let i = 0; i < keys.length; i += 1) {
    const segment = segments[keys[i]];
    if (
      segment?.start
      && segment?.finish
      && requestedStart >= segment.start.toSeconds()
      && requestedFinish <= segment.finish.toSeconds()
      && segment.aggregation === aggregation
    ) {
      return segment;
    }
  }

  return null;
};

/**
 * Extract a subset of combinedData filtered to the requested timeRange.
 * Filters time-series arrays on each meter node without mutating the source.
 *
 * Every filtered entity is normalised to a `{ start, finish }` range and retained when that
 * range overlaps the requested range, using the shared `hasTimeRangeOverlap` predicate. A
 * measurement bucket is kept when any part of it falls inside the view; a rule is kept when it
 * is in force for any part of the view. The boundaries are half-open, so a range finishing
 * exactly at the requested start has already elapsed and is dropped, and a range starting
 * exactly at the requested finish has not begun and is dropped.
 *
 * Normalisation per entity:
 * - `dataConsumed.data` / `dataGenerated.data` — `{ start: getIntervalStart(timestamp,
 *   aggregation, timezone), finish: timestamp }`, where `aggregation` comes from the
 *   container; `timestamp` is the trailing edge of the interval the point covers
 * - `tradeSetSummary[].data` — `range` as supplied, both boundaries used
 * - `rules.edges` — each rule's own `start`/`finish`
 *
 * `null` means two different things and the difference is not visible in the value. On a rule
 * it means unbounded — an ongoing or backdated rule — and must never exclude, which
 * `hasTimeRangeOverlap` already handles. On a measurement it means malformed, and must be
 * rejected before the predicate sees it; see `isValidTimeRange`. That asymmetry is the only
 * kind-specific logic here.
 *
 * `hasTimeRangeOverlap` tests truthiness rather than nullness, so an epoch of `0` is read as
 * unbounded there while `isValidTimeRange` accepts it. Harmless for real data, but the two
 * disagree about `0`.
 *
 * All other fields (meter metadata, property info) are copied as-is.
 * @param {object} combinedData - Cached combinedData from a segment.
 * @param {object} timeRange - Requested range with `start` and `finish` Luxon DateTimes.
 * @param {string} timezone - IANA zone the data was aggregated in, required to size a `P1D`
 *   bucket (see `getIntervalStart`). PropertyShow resolves it from the property, falling back
 *   to the partner default and then the system zone, so it is always present.
 * @returns {object} New combinedData object scoped to the requested range.
 */
export const extractDataFromCache = (combinedData, timeRange, timezone) => {
  if (!combinedData?.property?.meters?.edges || !timeRange?.start || !timeRange?.finish) {
    return combinedData;
  }

  if (!timezone) {
    // PropertyShow always resolves a timezone before reading the cache (see JSDoc above), so
    // reaching this branch means that invariant broke somewhere upstream. Returning the data
    // unfiltered rather than throwing keeps the chart rendering, but it silently reintroduces
    // the over-reporting bug this function exists to fix, so it must be visible.
    logger.warn('extractDataFromCache: missing timezone — returning combinedData unfiltered');
    return combinedData;
  }

  const requestedRange = {
    start: Math.floor(timeRange.start.toSeconds()),
    finish: Math.floor(timeRange.finish.toSeconds()),
  };

  const filteredEdges = combinedData.property.meters.edges.map((edge) => {
    const node = edge?.node;
    if (!node) return edge;

    const getFilteredTimeSeries = (dataObj) => {
      if (!dataObj?.data) return dataObj;
      if (dataObj.data.length > 0 && !getAggregationDuration(dataObj.aggregation)) {
        // Every point is about to be dropped: `getIntervalStart` has no bucket width to derive
        // a start from, so `isValidTimeRange` rejects every point below as malformed. Logged
        // once per container rather than per point.
        logger.warn(
          `extractDataFromCache: unrecognised aggregation "${dataObj.aggregation}" — dropping all ${dataObj.data.length} points`,
        );
      }
      return {
        ...dataObj,
        data: dataObj.data.filter((point) => {
          const range = {
            start: getIntervalStart(point?.timestamp, dataObj.aggregation, timezone),
            finish: point?.timestamp,
          };
          return isValidTimeRange(range) && hasTimeRangeOverlap(range, requestedRange);
        }),
      };
    };

    const getFilteredTradeSummary = (summaryArray) => {
      if (!Array.isArray(summaryArray)) return summaryArray;
      return summaryArray.map((trade) => {
        if (!Array.isArray(trade?.data)) return trade;
        return {
          ...trade,
          data: trade.data.filter((point) => {
            const range = point?.range;
            return !!range && isValidTimeRange(range)
              && hasTimeRangeOverlap(range, requestedRange);
          }),
        };
      });
    };

    const getFilteredRules = (rulesObj) => {
      if (!rulesObj?.edges) return rulesObj;
      return {
        ...rulesObj,
        edges: rulesObj.edges.filter(
          (ruleEdge) => !!ruleEdge?.node && hasTimeRangeOverlap(ruleEdge.node, requestedRange),
        ),
      };
    };

    return {
      node: {
        ...node,
        [DATA_CONSUMED_KEY]: getFilteredTimeSeries(node[DATA_CONSUMED_KEY]),
        [DATA_GENERATED_KEY]: getFilteredTimeSeries(node[DATA_GENERATED_KEY]),
        tradeSetSummary: getFilteredTradeSummary(node.tradeSetSummary),
        rules: getFilteredRules(node.rules),
      },
    };
  });

  return {
    ...combinedData,
    property: {
      ...combinedData.property,
      meters: {
        ...combinedData.property.meters,
        edges: filteredEdges,
      },
    },
  };
};

/**
 * Merge two sorted time-series arrays into one with unique timestamps.
 * If both arrays contain a point at the same timestamp, the point from
 * latestData (the newer segment) is kept and the one from existingData
 * is discarded.
 * @param {Array} existingData - Existing data array (points with
 *   `timestamp`).
 * @param {Array} latestData - Newer data array to merge in.
 * @returns {Array} Merged array with unique timestamps, sorted ascending.
 */
export const mergeUniqueTimeSeries = (existingData, latestData) => {
  const merged = [...(existingData || []), ...(latestData || [])];
  const seen = new Map();
  merged.forEach((point) => {
    seen.set(point.timestamp, point);
  });
  return Array.from(seen.values())
    .sort((a, b) => a.timestamp - b.timestamp);
};

/**
 * Build a composite key string for a trade set summary entry.
 * @param {object} key - The trade summary key object.
 * @returns {string} Composite key for map lookups.
 */
export const buildTradeKey = (key) => {
  const k = key || {};
  const {
    direction, ruleId, tradePointId, type,
  } = k;
  return `${tradePointId}_${ruleId}_${direction}_${type}`;
};

/**
 * Merge two trade summary data arrays into one with unique `range.finish`
 * values. If both arrays contain an entry with the same finish, the entry
 * from latestData (the newer segment) is kept and the one from
 * existingData is discarded.
 * @param {Array} existingData - Existing data array.
 * @param {Array} latestData - Newer data array to merge in.
 * @returns {Array} Merged array with unique range.finish, sorted ascending.
 */
export const mergeUniqueTradeSummaryData = (existingData, latestData) => {
  const merged = [...(existingData || []), ...(latestData || [])];
  const seen = new Map();
  merged.forEach((point) => {
    const finish = point?.range?.finish;
    if (finish != null) {
      seen.set(finish, point);
    }
  });
  return Array.from(seen.values())
    .sort((a, b) => (a?.range?.finish ?? 0) - (b?.range?.finish ?? 0));
};

/**
 * Merge two combinedData structures produced by the PropertyShow data
 * pipeline. Combines meter-level time-series arrays, keeping only unique
 * data points. When both structures contain a point at the same timestamp
 * (or trade range), the point from latestData (the newer segment) is
 * kept.
 * Meters present in only one structure are carried through unchanged.
 * @param {object} existingData - Existing combinedData
 *   `{ property: { meters: { edges: [{ node }] } } }`.
 * @param {object} latestData - Newer combinedData to merge in
 *   (same shape).
 * @returns {object} New combinedData with merged meter data.
 */
export const mergeCombinedData = (existingData, latestData) => {
  const existingEdges = existingData?.property?.meters?.edges;
  if (!existingEdges) return latestData;

  const latestEdges = latestData?.property?.meters?.edges;
  if (!latestEdges) return existingData;

  // Index latestData meters by uuid for O(1) lookup
  const latestByUuid = new Map();
  latestEdges.forEach((edge) => {
    if (edge?.node?.uuid) {
      latestByUuid.set(edge.node.uuid, edge.node);
    }
  });

  // Track which latestData meters were matched so we can append
  // unmatched ones
  const matchedUuids = new Set();

  const mergedEdges = existingEdges.map((edge) => {
    const existingNode = edge?.node;
    if (!existingNode?.uuid) return edge;

    const latestNode = latestByUuid.get(existingNode.uuid);
    if (!latestNode) return edge;

    matchedUuids.add(existingNode.uuid);

    // Merge consumed
    const mergedConsumed = {
      ...(existingNode[DATA_CONSUMED_KEY] || {}),
      ...(latestNode[DATA_CONSUMED_KEY] || {}),
      data: mergeUniqueTimeSeries(
        existingNode[DATA_CONSUMED_KEY]?.data,
        latestNode[DATA_CONSUMED_KEY]?.data,
      ),
    };

    // Merge generated
    const mergedGenerated = {
      ...(existingNode[DATA_GENERATED_KEY] || {}),
      ...(latestNode[DATA_GENERATED_KEY] || {}),
      data: mergeUniqueTimeSeries(
        existingNode[DATA_GENERATED_KEY]?.data,
        latestNode[DATA_GENERATED_KEY]?.data,
      ),
    };

    // Merge trade set summary
    const existingTradeMap = new Map();
    (existingNode.tradeSetSummary || []).forEach((trade) => {
      existingTradeMap.set(buildTradeKey(trade.key), trade);
    });

    const mergedTrades = [
      ...(existingNode.tradeSetSummary || []),
    ];
    (latestNode.tradeSetSummary || []).forEach((trade) => {
      const tradeKey = buildTradeKey(trade.key);
      const existingTrade = existingTradeMap.get(tradeKey);
      if (existingTrade) {
        const idx = mergedTrades.indexOf(existingTrade);
        mergedTrades[idx] = {
          ...existingTrade,
          data: mergeUniqueTradeSummaryData(
            existingTrade.data,
            trade.data,
          ),
        };
      } else {
        mergedTrades.push(trade);
      }
    });

    // Merge rules (dedup by uuid)
    const mergedRulesEdges = [
      ...(existingNode.rules?.edges || []),
    ];
    const existingRuleIds = new Set(
      mergedRulesEdges.map((e) => e?.node?.uuid).filter(Boolean),
    );
    (latestNode.rules?.edges || []).forEach((ruleEdge) => {
      if (
        ruleEdge?.node?.uuid
        && !existingRuleIds.has(ruleEdge.node.uuid)
      ) {
        mergedRulesEdges.push(ruleEdge);
      }
    });

    return {
      node: {
        ...existingNode,
        ...latestNode,
        [DATA_CONSUMED_KEY]: mergedConsumed,
        [DATA_GENERATED_KEY]: mergedGenerated,
        tradeSetSummary: mergedTrades,
        rules: { edges: mergedRulesEdges },
      },
    };
  });

  // Append meters that exist only in latestData
  latestEdges.forEach((edge) => {
    if (edge?.node?.uuid && !matchedUuids.has(edge.node.uuid)) {
      mergedEdges.push(edge);
    }
  });

  return {
    ...existingData,
    property: {
      ...existingData.property,
      ...latestData.property,
      meters: {
        ...existingData.property.meters,
        edges: mergedEdges,
      },
    },
  };
};

/**
 * Store a fetch result as a cache segment. Before inserting, checks for
 * existing segments with the same aggregation that are adjacent
 * to or overlap with the new data. If found, merges the combinedData and
 * expands the date range to the union, replacing the old segment(s).
 * Evicts the oldest segment (FIFO) when at capacity
 * ({@link MAX_CACHE_SEGMENTS}).
 * Mutates `cache.segments` in place — designed to work with React refs
 * so the caller does not need to clone the cache on every insert.
 * @param {object} cacheObj - The cache object (with `segments`).
 * @param {object} span - The date range with `start` and `finish` Luxon DateTimes.
 * @param {object} mergedData - The combinedData to cache.
 * @param {string} aggregation - Aggregation level (e.g. 'PT30M', 'P1D').
 */
export const addSegmentToCache = (cacheObj, span, mergedData, aggregation) => {
  if (!mergedData?.property?.meters?.edges) return;

  const { segments } = cacheObj;

  let finalStart = span.start;
  let finalFinish = span.finish;
  let finalData = mergedData;

  // Find and merge with any adjacent/overlapping same-aggregation
  // segments. Loop until no more mergeable segments remain
  // (chain merge: A+B then C adjacent to merged AB).
  let merged = true;
  while (merged) {
    merged = false;
    const keys = Object.keys(segments);
    for (let i = 0; i < keys.length; i += 1) {
      const seg = segments[keys[i]];
      if (seg.aggregation !== aggregation) {
        // eslint-disable-next-line no-continue
        continue;
      }

      const segStartSec = seg.start.toSeconds();
      const segFinishSec = seg.finish.toSeconds();
      const newStartSec = finalStart.toSeconds();
      const newFinishSec = finalFinish.toSeconds();

      // Ranges are half-open `[start, finish)`, so segments are contiguous when
      // they overlap or touch exactly (one's finish equals the other's start).
      // Any larger gap is uncached time and must not be merged over.
      const contiguous = segStartSec <= newFinishSec
        && newStartSec <= segFinishSec;

      if (contiguous) {
        finalData = mergeCombinedData(seg.combinedData, finalData);
        finalStart = segStartSec < newStartSec
          ? seg.start : finalStart;
        finalFinish = segFinishSec > newFinishSec
          ? seg.finish : finalFinish;

        delete segments[keys[i]];
        merged = true;
        break; // restart loop after mutation
      }
    }
  }

  const startSec = Math.floor(finalStart.toSeconds());
  const finishSec = Math.floor(finalFinish.toSeconds());
  const segmentKey = `${startSec}_${finishSec}`;

  if (Object.keys(segments).length >= MAX_CACHE_SEGMENTS) {
    delete segments[Object.keys(segments)[0]];
  }

  segments[segmentKey] = {
    start: finalStart,
    finish: finalFinish,
    combinedData: finalData,
    aggregation,
    lastUpdatedAt: Date.now(),
  };

  logger.log(
    'Cached segment:',
    segmentKey,
    'with',
    finalData.property.meters.edges.length,
    'meters',
  );
};

// ── High-level Cache Layer API ──────────────────────────────────
// Consumers (hooks, components) should call only these functions.
// The functions above are internal helpers used by this layer.

/**
 * Look up a cached segment that covers the requested timeRange and
 * aggregation level. If found, extracts and returns the data scoped
 * to the requested range. This is the only data the read path exposes —
 * there is no unfiltered side-channel, so a UI count derived from the
 * result always matches what was actually rendered from it.
 * @param {object} cacheObj - The cache object.
 * @param {object} timeRange - Requested range with `start` and `finish`
 *   Luxon DateTimes.
 * @param {string} aggregation - Required aggregation level
 *   (e.g. 'PT30M', 'P1D').
 * @param {string} timezone - IANA zone used by `extractDataFromCache` to size
 *   `P1D` buckets when clipping the segment to the requested range.
 * @returns {object|null} `{ data }` on hit, or `null` on miss.
 */
export const lookupCache = (cacheObj, timeRange, aggregation, timezone) => {
  const segment = getSegmentDataFromCache(cacheObj.segments, timeRange, aggregation);
  if (!segment) return null;
  return {
    data: extractDataFromCache(segment.combinedData, timeRange, timezone),
  };
};

/**
 * Read-through cache: looks up the cache for the requested date range
 * and aggregation. On a cache hit, calls onDataFunc immediately with
 * the cached data. On a miss, delegates to getPropertyDataInBatches,
 * stores the result, then calls onDataFunc.
 *
 * ## Cache bypass
 * To skip caching entirely, import and call `getPropertyDataInBatches`
 * (from `./dataBatching`) directly instead of this function. No other
 * code changes are needed.
 * @param {object} params - Function parameters.
 * @param {string} params.propertyId - The property UUID. When the
 *   property changes the module-level cache is automatically reset.
 * @param {object} params.timeRange - { start, finish } Luxon DateTimes.
 * @param {string} params.aggregation - e.g. 'PT30M', 'P1D'.
 * @param {object} params.fetchParams - Extra params forwarded to
 *   getPropertyDataInBatches (propertyId, timezone, environment, etc.).
 *   `fetchParams.timezone` is used on a cache hit to size `P1D` buckets when
 *   clipping the segment to the requested range.
 * @param {Function} params.onDataFunc - Called with (data, fromCache) on
 *   both hit and miss.
 * @param {Function} params.onErrorFunc - (error, message) => void.
 * @param {Function} [params.onBeforeFetchFunc] - Called on cache miss
 *   before the fetch starts. Receives ({ segmentsExist }).
 * @param {object} params.isFetchingRef - Ref tracking in-flight state.
 * @param {object} params.cancelledRef - Ref for cancellation.
 * @param {Function} [params.stateDispatchFunc] - Passed through to
 *   fetchFunc for batch progress dispatches.
 */
export const getPropertyDataFromCache = ({
  propertyId,
  timeRange,
  aggregation,
  fetchParams,
  onDataFunc,
  onErrorFunc,
  onBeforeFetchFunc,
  isFetchingRef,
  cancelledRef,
  stateDispatchFunc,
}) => {
  // Reset cache when propertyId changes
  if (cache.propertyId !== propertyId) {
    cache = initialiseCache(propertyId);
  }

  // 1. Check cache
  const cacheHit = lookupCache(cache, timeRange, aggregation, fetchParams?.timezone);
  if (cacheHit) {
    logger.log('Cache hit - serving data from cached segment');
    onDataFunc(cacheHit.data, true);
    return;
  }

  // 2. Cache miss — notify caller, then fetch
  logger.log(
    'Cache miss - fetching data for date range:',
    timeRange.start.toISODate(),
    'to',
    timeRange.finish.toISODate(),
  );

  const segmentsExist = Object.keys(cache.segments).length > 0;

  if (onBeforeFetchFunc) {
    onBeforeFetchFunc({ segmentsExist });
  }

  // 3. Delegate to getPropertyDataInBatches
  getPropertyDataInBatches({
    ...fetchParams,
    timeRange,
    historianAggregation: aggregation,
    isFetchingRef,
    cancelledRef,
    stateDispatchFunc,
    onDataReceived: (mergedData) => {
      addSegmentToCache(cache, timeRange, mergedData, aggregation);
      onDataFunc(mergedData, false);
    },
    onError: onErrorFunc,
  });
};
